import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../api/axiosInstance';

const DUMMY_TOKEN = 'dummy-token-valoria-demo';
const isDummyMode = () => {
  try { return localStorage.getItem('token') === DUMMY_TOKEN; } catch { return false; }
};

// ---------- DUMMY DATA ----------
const DUMMY_AGENT = {
  id: 'dummy-agent-1', agent_id: 'dummy-agent-1',
  agent_name: 'Demo Agent', name: 'Demo Agent',
  agent_type: 'customer_support', type: 'customer_support',
  is_active: true, status: 'active',
  total_calls: 48, success_rate: '87%', success_rate_value: 87,
  average_call_duration: '2:34', average_call_duration_seconds: 154,
  individual_results: [],
};
const DUMMY_ANALYTICS = {
  user_info: null,
  overview: {
    total_calls: 248, success_rate: '87%', success_rate_value: 87,
    average_call_duration: '2:34', average_call_duration_seconds: 154,
    active_agent_count: 3,
  },
  call_patterns: [
    { hour: 9, calls: 12 }, { hour: 10, calls: 19 }, { hour: 11, calls: 25 },
    { hour: 14, calls: 31 }, { hour: 15, calls: 28 }, { hour: 16, calls: 22 },
  ],
  weekly_performance: [
    { day: 'Mon', calls: 34 }, { day: 'Tue', calls: 41 }, { day: 'Wed', calls: 38 },
    { day: 'Thu', calls: 52 }, { day: 'Fri', calls: 46 }, { day: 'Sat', calls: 18 }, { day: 'Sun', calls: 9 },
  ],
  agent_performance: [{ agent_name: 'Demo Agent', total_calls: 248, success_rate: '87%' }],
  agent_types: { customer_support: 3 },
};
const DUMMY_SUBSCRIPTION = {
  plan: 'pro', plan_name: 'Pro', status: 'active',
  included_minutes: 500, minutes_used: 124,
};
const DUMMY_AGENTS = [DUMMY_AGENT];
const DUMMY_PLANS = [
  {
    id: 'plan-free', code: 'free', name: 'Free',
    monthly_price: 0, setup_fee: 0,
    included_minutes: 60, extra_minute_rate: 0.12,
    features: { 'Voice Agents': true, 'Analytics Dashboard': true, 'Email Support': true, 'Custom Voices': false, 'API Access': false, 'Priority Support': false },
  },
  {
    id: 'plan-pro', code: 'pro', name: 'Pro', is_popular: true,
    monthly_price: 99, setup_fee: 0,
    included_minutes: 500, extra_minute_rate: 0.08,
    features: { 'Voice Agents': true, 'Analytics Dashboard': true, 'Email Support': true, 'Custom Voices': true, 'API Access': true, 'Priority Support': false },
  },
  {
    id: 'plan-enterprise', code: 'enterprise', name: 'Enterprise',
    monthly_price: null, setup_fee: null,
    included_minutes: 'Unlimited', extra_minute_rate: null,
    features: { 'Voice Agents': true, 'Analytics Dashboard': true, 'Email Support': true, 'Custom Voices': true, 'API Access': true, 'Priority Support': true },
  },
];
// --------------------------------

const pickErr = (e, fallback = 'Something went wrong') => {
  const d = e?.response?.data;
  if (Array.isArray(d?.detail) && d.detail.length) {
    const first = d.detail[0];
    if (typeof first === 'string') return first;
    if (first?.msg) return first.msg;
    if (first?.message) return first.message;
  }
  return d?.detail || d?.message || e?.message || fallback;
};

const appendToFormData = (fd, key, val) => {
  if (val == null) return;
  if (typeof File !== 'undefined' && val instanceof File) { fd.append(key, val); return; }
  if (typeof Blob !== 'undefined' && val instanceof Blob) { fd.append(key, val); return; }
  if (Array.isArray(val)) { val.forEach((v) => appendToFormData(fd, key, v)); return; }
  if (typeof val === 'boolean') { fd.append(key, val ? 'true' : 'false'); return; }
  if (typeof val === 'number' || typeof val === 'string') { fd.append(key, String(val)); return; }
  try { fd.append(key, JSON.stringify(val)); } catch { fd.append(key, String(val)); }
};

const normalizePhone = (s) => {
  const raw = String(s || '').trim();
  if (!raw) return '';
  const digits = raw.replace(/[^\d+]/g, '');
  const hasPlus = digits.startsWith('+');
  const body = digits.replace(/^\+/, '');
  return hasPlus ? `+${body}` : (body ? `+${body}` : '');
};

const unixToISO = (u) => {
  if (u == null || Number.isNaN(Number(u))) return null;
  const n = Number(u);
  const ms = n < 2000000000 ? n * 1000 : n;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const snake = (name) => String(name || '').trim().toLowerCase().replace(/\s+/g, '_');

const normalizePlan = (p) => {
  const globalFeatures = {
    'Inbound Calls': true,
    'Outbound Calls': true,
    'Lead Capture': true,
    'Calendar Integration': 'Google Calendar only',
    'Performance Dashboard': true,
    'Custom Greeting': true,
    'Voice Tone Presets': 'Standard + Custom',
    'Advanced Analytics': true,
  };

  const rawFeatures = (p?.features && typeof p.features === 'object') ? p.features : {};
  const normalizedKeysFeatures = {};

  // Normalize incoming keys to match globalFeatures format (Title Case)
  Object.entries(rawFeatures).forEach(([k, v]) => {
    const normalizedKey = k.toString().replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
    normalizedKeysFeatures[normalizedKey] = v;
  });

  // Combine them: Start with globalFeatures, then add others from the API.
  // If a global feature exists in the API, we still force the global value (always included).
  const combinedFeatures = { ...globalFeatures };
  Object.entries(normalizedKeysFeatures).forEach(([k, v]) => {
    if (!(k in combinedFeatures)) {
      combinedFeatures[k] = v;
    }
  });

  return {
    name: String(p?.name ?? '').trim(),
    setup_fee: p?.setup_fee ?? null,
    monthly_price: p?.monthly_price ?? null,
    included_minutes: p?.included_minutes ?? null,
    extra_minute_rate: p?.extra_minute_rate ?? null,
    features: combinedFeatures,
    id: p?.id ?? null,
    code: p?.code ?? null,
    is_popular: Boolean(p?.is_popular),
    recommended_for: p?.recommended_for ?? '',
    plan: p?.plan ?? null,
  };
};

const preferStatus = (job) => {
  const live = job?.elevenlabs_live_status?.status || '';
  const local = job?.local_record?.updated_status || job?.local_record?.previous_local_status || '';
  return String(live || local || 'pending').toLowerCase();
};

const numbersFrom = (job) => {
  const local = job?.local_record?.total_numbers;
  const live = job?.elevenlabs_live_status?.total_calls_scheduled ?? job?.elevenlabs_live_status?.total_calls_dispatched;
  return local ?? live ?? 0;
};

const createdAtFrom = (job) => job?.local_record?.created_at || unixToISO(job?.elevenlabs_live_status?.created_at_unix) || null;

const withScopeGuard = async (get, set, work) => {
  const scopeId = get().agentsScopeId;
  const res = await work();
  if (get().agentsScopeId !== scopeId) return { success: false, canceled: true };
  return res;
};

const weeklySeriesFrom = (weekly) => {
  const source = Array.isArray(weekly) ? weekly : [];
  const order = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const toKey = (raw) => {
    const s = String(raw ?? '').trim().toLowerCase();
    if (/^\d+$/.test(s)) {
      const n = Number(s);
      const idx = n >= 1 && n <= 7 ? n - 1 : (n >= 0 && n <= 6 ? n : 0);
      return order[idx];
    }
    if (s.startsWith('mon')) return 'Mon';
    if (s.startsWith('tue')) return 'Tue';
    if (s.startsWith('wed')) return 'Wed';
    if (s.startsWith('thu')) return 'Thu';
    if (s.startsWith('fri')) return 'Fri';
    if (s.startsWith('sat')) return 'Sat';
    if (s.startsWith('sun')) return 'Sun';
    return 'Mon';
  };
  const map = {};
  for (const x of source) {
    const key = toKey(x?.day);
    const calls = Number(x?.calls ?? 0);
    map[key] = (map[key] ?? 0) + (Number.isFinite(calls) ? calls : 0);
  }
  return order.map((d) => ({ day: d, calls: map[d] ?? 0 }));
};

// Helper to normalize email agent data
const normalizeEmailAgent = (agentData, userEmail = '') => {
  const agent = agentData?.agent || {};
  const analytics = agentData?.analytics || {};

  return {
    id: agent.id,
    agent_id: agent.id,
    name: agent.agent_name || '',
    email_address: agent.email_address || '',
    prompt: agent.agent_prompt || '',
    is_active: Boolean(agent.is_active),
    auto_response_enabled: Boolean(agent.auto_response_enabled),
    check_interval: agent.check_interval || 0,
    created_at: agent.created_at || null,
    updated_at: agent.updated_at || null,
    user_id: agent.user_id,
    user_email: agentData?.user_email || userEmail || '',

    // Analytics
    total_emails_checked: analytics.total_emails_checked || 0,
    total_emails_responded: analytics.total_emails_responded || 0,
    total_spam_detected: analytics.total_spam_detected || 0,
    response_rate: analytics.response_rate || '0%',
    spam_rate: analytics.spam_rate || '0%',
    last_check_time: analytics.last_check_time || null,
    last_email_processed: analytics.last_email_processed || null,
    total_errors: analytics.total_errors || 0,
  };
};

// Helper to normalize SMS agent data
const normalizeSMSAgent = (agentData, userEmail = '') => {
  const agent = agentData?.agent || {};
  const analytics = agentData?.analytics || {};

  return {
    id: agent.id,
    agent_id: agent.id,
    agent_name: agent.agent_name || '',
    phone_number: agent.phone_number || '',
    phone_number_sid: agent.phone_number_sid || '',
    agent_prompt: agent.agent_prompt || '',
    prompt: agent.agent_prompt || '',
    is_active: Boolean(agent.is_active),
    auto_response_enabled: Boolean(agent.auto_response_enabled),
    created_at: agent.created_at || null,
    updated_at: agent.updated_at || null,
    user_id: agent.user_id,
    user_email: agentData?.user_email || userEmail || '',

    // Analytics
    total_messages_received: analytics.total_messages_received || 0,
    total_messages_sent: analytics.total_messages_sent || 0,
    response_rate: analytics.response_rate || '0%',
    total_errors: analytics.total_errors || 0,
    last_message_time: analytics.last_message_time || null,
  };
};

const DASHBOARD_CACHE_TTL_MS = 60_000; // 1 minute

const useAppStore = create(
  persist(
    (set, get) => ({
      sidebarOpen: true,
      currentPage: 'dashboard',
      loading: false,

      // Per-feature loading flags (avoids global loading flicker)
      agentsLoading: false,
      analyticsLoading: false,
      subscriptionLoading: false,
      dashboardLoading: false,

      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setCurrentPage: (page) => set({ currentPage: page }),
      setLoading: (loading) => set({ loading }),
      setSelectedAgent: (agent) => set({ selectedAgent: agent }),

      // Shared dashboard-analysis cache
      dashboardData: null,
      dashboardFetchedAt: null,
      dashboardFetching: false,

      agents: [],
      selectedAgent: null,
      adminUserInfo: null,
      agentsLoaded: false,
      agentTypeCache: {}, // agent_name → agent_type, persisted locally since backend doesn't return agent_type in list

      // Email agents state
      emailAgents: [],
      emailAgentsLoaded: false,
      emailAgentsError: '',

      // Email check state
      emailCheckResult: null,
      emailCheckLoading: false,
      emailCheckError: '',

      // SMS agents state
      smsAgents: [],
      smsAgentsLoaded: false,
      smsAgentsError: '',

      // SMS messages state
      smsAgentMessages: [],
      smsMessagesLoading: false,
      smsMessagesError: '',

      agentTypes: [
        { id: 'customer_support', name: 'Customer Support', icon: '🎧' },
        { id: 'appointment_scheduling', name: 'Appointment Scheduling', icon: '📅' },
        { id: 'lead_generation', name: 'Lead Generation', icon: '🎯' },
      ],

      subscriptions: [],
      currentSubscription: null,

      notifications: {
        items: [],
        unreadCount: 0,
        loading: false,
        error: '',
      },

      analytics: {
        user_info: null,
        overview: {
          total_calls: 0,
          success_rate: '0%',
          success_rate_value: 0,
          average_call_duration: '0:00',
          average_call_duration_seconds: 0,
          active_agent_count: 0,
        },
        call_patterns: [],
        weekly_performance: [],
        agent_performance: [],
        agent_types: {},
        data_period: null,
      },

      agentOverview: null,
      numbersAnalytics: null,
      agentAnalytics: {},

      usersAdmin: [],
      totalUsersAdmin: 0,
      adminUsersError: '',

      agentsScope: { type: 'self', email: null },
      agentsScopeId: 0,

      batchCalling: {
        jobs: [],
        loading: false,
        error: '',
        total_jobs: 0,
        successful_status_updates: 0,
        failed_status_updates: 0,
        lastFetched: null,
        creating: false,
        createError: '',
        lastCreateResponse: null,
      },

      quickCall: async ({ phone_number, email }) => {
        const pn = normalizePhone(phone_number);
        if (!pn) return { success: false, error: 'phone_number is required (E.164 like +923001234567)' };
        if (!email) return { success: false, error: 'email is required' };

        try {
          const params = new URLSearchParams();
          params.set('phone_number', pn);
          params.set('email', String(email).trim());

          const res = await api.post('/auth/agent/quick-call', params, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          });

          return { success: true, data: res?.data };
        } catch (e) {
          return { success: false, error: pickErr(e, 'Failed to create quick call') };
        }
      },

      createBatchCallingJob: async ({
        agent_name,
        call_name,
        csvOrExcelFile,
        phone_column = 'phone',
        scheduled_time_unix,
      } = {}) => {
        if (!agent_name) return { success: false, error: 'agent_name is required' };
        if (!call_name) return { success: false, error: 'call_name is required' };
        if (!csvOrExcelFile) return { success: false, error: 'csv_file is required' };

        set((s) => ({
          batchCalling: { ...s.batchCalling, creating: true, createError: '', lastCreateResponse: null },
        }));

        try {
          const fd = new FormData();
          appendToFormData(fd, 'agent_name', agent_name);
          appendToFormData(fd, 'call_name', call_name);
          appendToFormData(fd, 'csv_file', csvOrExcelFile);
          appendToFormData(fd, 'phone_column', phone_column);

          if (scheduled_time_unix != null) {
            appendToFormData(fd, 'scheduled_time_unix', String(scheduled_time_unix));
          }

          const res = await api.post('/agents/batch-calling', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const payload = res?.data ?? null;

          set((s) => ({
            batchCalling: { ...s.batchCalling, creating: false, createError: '', lastCreateResponse: payload },
          }));

          try { await get().fetchBatchCallingStatus(); } catch { }

          return { success: true, data: payload };
        } catch (e) {
          const msg = pickErr(e, 'Failed to create batch calling job');
          set((s) => ({ batchCalling: { ...s.batchCalling, creating: false, createError: msg } }));
          return { success: false, error: msg };
        }
      },

      fetchBatchCallingStatus: async () => {
        if (isDummyMode()) {
          set((s) => ({ batchCalling: { ...s.batchCalling, jobs: [], loading: false, error: '', total_jobs: 0 } }));
          return { success: true, jobs: [] };
        }
        set((s) => ({ batchCalling: { ...s.batchCalling, loading: true, error: '' } }));
        try {
          const res = await api.get('/agents/batch-calling-status');
          const d = res?.data || {};

          const jobs = Array.isArray(d.jobs)
            ? d.jobs.map((raw) => ({
              batch_job_id: raw.batch_job_id,
              call_name: raw.call_name,
              agent_name: raw.agent_name,
              agent_id: raw.agent_id,
              total_numbers: numbersFrom(raw),
              status: preferStatus(raw),
              created_at: createdAtFrom(raw),
              _raw: raw,
            }))
            : [];

          set((s) => ({
            batchCalling: {
              ...s.batchCalling,
              jobs,
              loading: false,
              error: '',
              total_jobs: Number(d.total_jobs ?? jobs.length),
              successful_status_updates: Number(d.successful_status_updates ?? 0),
              failed_status_updates: Number(d.failed_status_updates ?? 0),
              lastFetched: new Date().toISOString(),
            },
          }));

          return { success: true, jobs };
        } catch (e) {
          const msg = pickErr(e, 'Failed to fetch batch calling status');
          set((s) => ({ batchCalling: { ...s.batchCalling, loading: false, error: msg } }));
          return { success: false, error: msg };
        }
      },

      cancelBatchCalling: async (input) => {
        const raw = typeof input === 'string' ? input : input?.call_name;
        const call_name = String(raw ?? '').replace(/\s+/g, ' ').trim();
        if (!call_name) return { success: false, error: 'call_name is required' };

        const postJSON = async () =>
          api.post(
            '/agents/cancel-batch-calling',
            { call_name },
            {
              headers: { 'Content-Type': 'application/json' },
              transformRequest: [(data, headers) => {
                headers['Content-Type'] = 'application/json';
                return JSON.stringify(data);
              }],
            }
          );

        try {
          let res;
          try {
            res = await postJSON();
          } catch (e1) {
            const maybeFieldRequired = e1?.response?.status === 422;
            if (!maybeFieldRequired) throw e1;

            const params = new URLSearchParams();
            params.set('call_name', call_name);
            try {
              res = await api.post('/agents/cancel-batch-calling', params, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              });
            } catch (e2) {
              const fd = new FormData();
              fd.append('call_name', call_name);
              res = await api.post('/agents/cancel-batch-calling', fd);
            }
          }

          try { await get().fetchBatchCallingStatus(); } catch { }
          return { success: true, data: res?.data };
        } catch (e) {
          return { success: false, error: pickErr(e, 'Failed to cancel batch calling') };
        }
      },

      retryBatchCalling: async (input) => {
        const raw = typeof input === 'string' ? input : input?.call_name;
        const call_name = String(raw ?? '').replace(/\s+/g, ' ').trim();
        if (!call_name) return { success: false, error: 'call_name is required' };

        const postJSON = async () =>
          api.post(
            '/agents/retry-batch-calling',
            { call_name },
            {
              headers: { 'Content-Type': 'application/json' },
              transformRequest: [(data, headers) => {
                headers['Content-Type'] = 'application/json';
                return JSON.stringify(data);
              }],
            }
          );

        try {
          let res;
          try {
            res = await postJSON();
          } catch (e1) {
            const maybeFieldRequired = e1?.response?.status === 422;
            if (!maybeFieldRequired) throw e1;
            const params = new URLSearchParams();
            params.set('call_name', call_name);
            try {
              res = await api.post('/agents/retry-batch-calling', params, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              });
            } catch (e2) {
              const fd = new FormData();
              fd.append('call_name', call_name);
              res = await api.post('/agents/retry-batch-calling', fd);
            }
          }

          try { await get().fetchBatchCallingStatus(); } catch { }
          return { success: true, data: res?.data };
        } catch (e) {
          return { success: false, error: pickErr(e, 'Failed to retry batch calling') };
        }
      },

      updateSubscription: (sub) => set({ currentSubscription: sub }),

      setAgentsScope: (scope) =>
        set((s) => ({ agentsScope: scope, agentsScopeId: s.agentsScopeId + 1 })),
      initAgentsScope: ({ fromUser, urlEmail, isSuperAdmin }) => {
        const v = (fromUser && urlEmail)
          ? { type: 'user', email: urlEmail }
          : { type: 'self', email: null };
        set((s) => ({ agentsScope: v, agentsScopeId: s.agentsScopeId + 1 }));
        return v;
      },

      loadAgentsForCurrentScope: async () => withScopeGuard(get, set, async () => {
        if (isDummyMode()) {
          set({ agents: DUMMY_AGENTS, adminUserInfo: null, agentsLoading: false, agentsLoaded: true });
          return { success: true, data: DUMMY_AGENTS };
        }
        const scope = get().agentsScope;
        set({ agentsLoading: true, agentsLoaded: false });

        try {
          if (scope.type === 'user' && scope.email) {
            const res = await api.get('/auth/admin/user-agents', { params: { email: scope.email } });
            const data = res?.data || {};
            const rows = Array.isArray(data.agents) ? data.agents : [];
            set({
              agents: rows,
              adminUserInfo: {
                user_email: data.user_email,
                user_name: data.user_name,
                user_id: data.user_id,
                total_agents: data.total_agents ?? rows.length,
              },
              agentsLoading: false,
              agentsLoaded: true,
            });
            return { success: true, data: rows };
          }

          // Use cached dashboard data instead of a fresh API call
          const result = await get().fetchDashboardAnalysis();
          const rawAgents = result?.data?.your_ai_agents?.test_speed || [];
          const typeCache = get().agentTypeCache || {};
          const rows = rawAgents.map((a) => ({
            agent_id: a.agent_id,
            agent_name: a.agent_name,
            agent_type: typeCache[a.agent_name] || a.agent_type || 'unknown',
            type: typeCache[a.agent_name] || a.agent_type || 'unknown',
            is_active: Boolean(a.active),
            total_calls: a.calls ?? 0,
            success_rate: a.success_rate ?? 0,
            success_rate_value: a.success_rate ?? 0,
          }));
          set({ agents: rows, adminUserInfo: null, agentsLoading: false, agentsLoaded: true });
          return { success: true, data: rows };
        } catch (err) {
          set({ agentsLoading: false, agentsLoaded: true });
          return { success: false, error: err?.response?.data?.message || err.message };
        }
      }),

      loadEmailAgentsForCurrentScope: async () => withScopeGuard(get, set, async () => {
        const scope = get().agentsScope;
        set({ loading: true, emailAgentsLoaded: false, emailAgentsError: '' });

        try {
          if (scope.type === 'user' && scope.email) {
            const res = await api.get('/email-agent/saas/admin/user-email-agents', {
              params: { email: scope.email }
            });
            const data = res?.data || {};
            const rows = Array.isArray(data.agents) ? data.agents : [];

            const normalized = rows.map(agentData =>
              normalizeEmailAgent(agentData, data.user_email)
            );

            set({
              emailAgents: normalized,
              adminUserInfo: {
                user_email: data.user_email,
                user_name: data.user_name || '',
                total_agents: data.total_agents || rows.length,
              },
              loading: false,
              emailAgentsLoaded: true,
            });
            return { success: true, data: normalized };
          }

          const res = await api.get('/email-agent/saas/my-agents');
          const data = res?.data || {};
          const rows = Array.isArray(data.agents) ? data.agents : [];

          const normalized = rows.map(agentData =>
            normalizeEmailAgent(agentData, data.user_email)
          );

          set({
            emailAgents: normalized,
            adminUserInfo: null,
            loading: false,
            emailAgentsLoaded: true,
          });
          return { success: true, data: normalized };
        } catch (err) {
          const errorMsg = pickErr(err, 'Failed to load email agents');
          set({
            loading: false,
            emailAgentsLoaded: true,
            emailAgentsError: errorMsg
          });
          return { success: false, error: errorMsg };
        }
      }),

      checkEmailAgent: async (agentId) => {
        if (!agentId) return { success: false, error: 'Missing agent id' };

        set({
          emailCheckLoading: true,
          emailCheckError: '',
          emailCheckResult: null
        });

        try {
          const res = await api.post(`/email-agent/saas/check-agent/${agentId}`);
          const data = res?.data || {};

          set({
            emailCheckLoading: false,
            emailCheckResult: data
          });

          try { await get().loadEmailAgentsForCurrentScope(); } catch { }

          return { success: true, data };
        } catch (err) {
          const errorMsg = pickErr(err, 'Failed to check email agent');
          set({
            emailCheckLoading: false,
            emailCheckError: errorMsg
          });
          return { success: false, error: errorMsg };
        }
      },

      clearEmailCheckResult: () => set({
        emailCheckResult: null,
        emailCheckError: ''
      }),

      deleteEmailAgent: async (agentId) => {
        if (!agentId) return { success: false, error: 'Missing agent id' };
        try {
          await api.delete(`/email-agent/saas/delete-agent/${agentId}`);
          set((s) => ({
            emailAgents: (s.emailAgents || []).filter((a) => a.id !== agentId && a.agent_id !== agentId),
          }));
          return { success: true };
        } catch (err) {
          return { success: false, error: pickErr(err, 'Failed to delete email agent') };
        }
      },

      updateEmailAgentStatus: async (agentId, is_active) => {
        if (!agentId) return { success: false, error: 'Missing agent id' };
        try {
          await api.put(`/email-agent/saas/update-agent/${agentId}`, {
            is_active: is_active
          });
          set((s) => ({
            emailAgents: (s.emailAgents || []).map((a) =>
              a.id === agentId || a.agent_id === agentId ? { ...a, is_active: is_active } : a
            ),
          }));
          return { success: true };
        } catch (err) {
          return { success: false, error: pickErr(err, 'Failed to update email agent status') };
        }
      },

      toggleEmailAgentAutoResponse: async (agentId, auto_response_enabled) => {
        if (!agentId) return { success: false, error: 'Missing agent id' };
        try {
          await api.put(`/email-agent/saas/update-agent/${agentId}`, {
            auto_response_enabled: auto_response_enabled
          });
          set((s) => ({
            emailAgents: (s.emailAgents || []).map((a) =>
              a.id === agentId || a.agent_id === agentId ? { ...a, auto_response_enabled: auto_response_enabled } : a
            ),
          }));
          return { success: true };
        } catch (err) {
          return { success: false, error: pickErr(err, 'Failed to update auto-response') };
        }
      },

      createEmailAgent: async (payload = {}) => {
        const {
          agent_name,
          email_address,
          agent_prompt,
          check_interval = 60,
          auto_response_enabled = true,
          email_password,
        } = payload;

        if (!agent_name) return { success: false, error: 'agent_name is required' };
        if (!email_address) return { success: false, error: 'email_address is required' };
        if (!agent_prompt) return { success: false, error: 'agent_prompt is required' };
        if (!email_password) return { success: false, error: 'email_password is required' };

        try {
          const requestBody = {
            agent_name,
            email_address,
            email_password,
            agent_prompt,
            check_interval: Number(check_interval),
            auto_response_enabled: Boolean(auto_response_enabled),
          };

          const res = await api.post('/email-agent/saas/register-agent', requestBody, {
            headers: { 'Content-Type': 'application/json' },
          });

          try { await get().loadEmailAgentsForCurrentScope(); } catch { }

          return { success: true, data: res?.data };
        } catch (e) {
          console.error('Email agent creation error:', e);
          return { success: false, error: pickErr(e, 'Failed to create email agent') };
        }
      },

      updateEmailAgent: async (agentId, payload = {}) => {
        if (!agentId) return { success: false, error: 'Missing agent id' };

        try {
          const requestBody = {
            agent_name: payload.agent_name || '',
            agent_prompt: payload.agent_prompt || '',
            check_interval: payload.check_interval !== undefined ? Number(payload.check_interval) : 60,
            is_active: payload.is_active !== undefined ? Boolean(payload.is_active) : true,
            auto_response_enabled: payload.auto_response_enabled !== undefined ? Boolean(payload.auto_response_enabled) : true,
          };

          if (payload.email_password && payload.email_password.trim() !== '') {
            requestBody.email_password = payload.email_password;
          }

          const res = await api.put(`/email-agent/saas/update-agent/${agentId}`, requestBody, {
            headers: { 'Content-Type': 'application/json' },
          });

          try { await get().loadEmailAgentsForCurrentScope(); } catch { }

          return { success: true, data: res?.data };
        } catch (e) {
          console.error('Email agent update error:', e);
          return { success: false, error: pickErr(e, 'Failed to update email agent') };
        }
      },

      // SMS Agent Methods
      loadSMSAgentsForCurrentScope: async () => withScopeGuard(get, set, async () => {
        const scope = get().agentsScope;
        set({ loading: true, smsAgentsLoaded: false, smsAgentsError: '' });

        try {
          if (scope.type === 'user' && scope.email) {
            const res = await api.get('/sms-agent/saas/admin/user-sms-agents', {
              params: { email: scope.email }
            });
            const data = res?.data || {};
            const rows = Array.isArray(data.agents) ? data.agents : [];

            const normalized = rows.map(agentData =>
              normalizeSMSAgent(agentData, data.user_email)
            );

            set({
              smsAgents: normalized,
              adminUserInfo: {
                user_email: data.user_email,
                user_name: data.user_name || '',
                total_agents: data.total_agents || rows.length,
              },
              loading: false,
              smsAgentsLoaded: true,
            });
            return { success: true, data: normalized };
          }

          const res = await api.get('/sms-agent/saas/my-agents');
          const data = res?.data || {};
          const rows = Array.isArray(data.agents) ? data.agents : [];

          const normalized = rows.map(agentData =>
            normalizeSMSAgent(agentData, data.user_email)
          );

          set({
            smsAgents: normalized,
            adminUserInfo: null,
            loading: false,
            smsAgentsLoaded: true,
          });
          return { success: true, data: normalized };
        } catch (err) {
          const errorMsg = pickErr(err, 'Failed to load SMS agents');
          set({
            loading: false,
            smsAgentsLoaded: true,
            smsAgentsError: errorMsg
          });
          return { success: false, error: errorMsg };
        }
      }),

      deleteSMSAgent: async (agentId) => {
        if (!agentId) return { success: false, error: 'Missing agent id' };
        try {
          await api.delete(`/sms-agent/saas/delete-agent/${agentId}`);
          set((s) => ({
            smsAgents: (s.smsAgents || []).filter((a) => a.id !== agentId && a.agent_id !== agentId),
          }));
          return { success: true };
        } catch (err) {
          return { success: false, error: pickErr(err, 'Failed to delete SMS agent') };
        }
      },

      updateSMSAgentStatus: async (agentId, is_active) => {
        if (!agentId) return { success: false, error: 'Missing agent id' };
        try {
          await api.put(`/sms-agent/saas/update-agent/${agentId}`, {
            is_active: is_active
          });
          set((s) => ({
            smsAgents: (s.smsAgents || []).map((a) =>
              a.id === agentId || a.agent_id === agentId ? { ...a, is_active: is_active } : a
            ),
          }));
          return { success: true };
        } catch (err) {
          return { success: false, error: pickErr(err, 'Failed to update SMS agent status') };
        }
      },

      toggleSMSAgentAutoResponse: async (agentId, auto_response_enabled) => {
        if (!agentId) return { success: false, error: 'Missing agent id' };
        try {
          await api.put(`/sms-agent/saas/update-agent/${agentId}`, {
            auto_response_enabled: auto_response_enabled
          });
          set((s) => ({
            smsAgents: (s.smsAgents || []).map((a) =>
              a.id === agentId || a.agent_id === agentId ? { ...a, auto_response_enabled: auto_response_enabled } : a
            ),
          }));
          return { success: true };
        } catch (err) {
          return { success: false, error: pickErr(err, 'Failed to update auto-response') };
        }
      },

      createSMSAgent: async (payload = {}) => {
        const {
          agent_name,
          agent_prompt,
          country = 'US',
          auto_response_enabled = true,
        } = payload;

        if (!agent_name) return { success: false, error: 'agent_name is required' };
        if (!agent_prompt) return { success: false, error: 'agent_prompt is required' };
        if (!country) return { success: false, error: 'country is required' };

        try {
          const requestBody = {
            agent_name,
            agent_prompt,
            country,
            auto_response_enabled: Boolean(auto_response_enabled),
          };

          const res = await api.post('/sms-agent/saas/buy-number', requestBody, {
            headers: { 'Content-Type': 'application/json' },
          });

          try { await get().loadSMSAgentsForCurrentScope(); } catch { }

          return { success: true, data: res?.data };
        } catch (e) {
          console.error('SMS agent creation error:', e);
          return { success: false, error: pickErr(e, 'Failed to create SMS agent') };
        }
      },

      updateSMSAgent: async (agentId, payload = {}) => {
        if (!agentId) return { success: false, error: 'Missing agent id' };

        try {
          const requestBody = {
            agent_name: payload.agent_name || '',
            agent_prompt: payload.agent_prompt || '',
            is_active: payload.is_active !== undefined ? Boolean(payload.is_active) : true,
            auto_response_enabled: payload.auto_response_enabled !== undefined ? Boolean(payload.auto_response_enabled) : true,
          };

          const res = await api.put(`/sms-agent/saas/update-agent/${agentId}`, requestBody, {
            headers: { 'Content-Type': 'application/json' },
          });

          try { await get().loadSMSAgentsForCurrentScope(); } catch { }

          return { success: true, data: res?.data };
        } catch (e) {
          console.error('SMS agent update error:', e);
          return { success: false, error: pickErr(e, 'Failed to update SMS agent') };
        }
      },

      fetchSMSAgentMessages: async (agentId, limit = 50) => {
        if (!agentId) return { success: false, error: 'Missing agent id' };

        set({ smsMessagesLoading: true, smsMessagesError: '' });

        try {
          const res = await api.get(`/sms-agent/saas/agent/${agentId}/messages`, {
            params: { limit }
          });

          const payload = res?.data || {};

          // Accept several possible response shapes: array at root, or object with a messages/data/results array
          let rawMessages = [];
          if (Array.isArray(payload)) rawMessages = payload;
          else if (Array.isArray(payload.messages)) rawMessages = payload.messages;
          else if (Array.isArray(payload.data)) rawMessages = payload.data;
          else if (Array.isArray(payload.results)) rawMessages = payload.results;

          const messages = (rawMessages || []).map((m) => {
            const possibleBodies = [
              m?.body,
              m?.message,
              m?.text,
              m?.content,
              m?.message?.body,
              m?.message?.text,
              m?.message?.content,
              m?.message_body,
              m?.body_text,
              m?.sms_body,
              m?.text_body,
              m?.content?.body,
              m?.data?.body,
            ];
            const rawBody = possibleBodies.find((x) => x != null && String(x).trim() !== '') ?? '';
            const body = rawBody == null ? '' : String(rawBody);
            const from_number = m?.from_number ?? m?.from ?? m?.fromNumber ?? '';
            const to_number = m?.to_number ?? m?.to ?? m?.toNumber ?? '';
            const direction = (m?.direction || '').toString().toLowerCase() || (m?.from_number ? 'inbound' : 'outbound');

            // Normalize timestamp: prefer ISO-like strings, otherwise convert unix seconds to ISO
            let timestamp = null;
            if (m?.timestamp != null) {
              if (typeof m.timestamp === 'number') timestamp = unixToISO(m.timestamp);
              else if (/^\d+$/.test(String(m.timestamp))) timestamp = unixToISO(Number(m.timestamp));
              else timestamp = m.timestamp;
            } else if (m?.created_at != null) {
              if (typeof m.created_at === 'number') timestamp = unixToISO(m.created_at);
              else if (/^\d+$/.test(String(m.created_at))) timestamp = unixToISO(Number(m.created_at));
              else timestamp = m.created_at;
            } else if (m?.created_at_unix != null) {
              timestamp = unixToISO(m.created_at_unix);
            }

            return {
              body,
              from_number,
              to_number,
              direction,
              timestamp,
              _raw: m,
            };
          });

          set({
            smsAgentMessages: messages,
            smsMessagesLoading: false,
          });

          return { success: true, data: messages };
        } catch (e) {
          console.error('SMS messages fetch error:', e);
          const errorMsg = pickErr(e, 'Failed to fetch SMS messages');
          set({
            smsMessagesLoading: false,
            smsMessagesError: errorMsg,
          });
          return { success: false, error: errorMsg };
        }
      },

      fetchAdminUsers: async () => {
        const guard = Symbol('adminUsersFetch');
        set({ loading: true, adminUsersError: '', __adminGuard: guard });
        try {
          const res = await api.get('/auth/admin/users');
          if (get().__adminGuard !== guard) return { success: false, canceled: true };
          const { total_users, users } = res?.data || {};
          set({
            usersAdmin: Array.isArray(users) ? users : [],
            totalUsersAdmin: Number(total_users ?? (Array.isArray(users) ? users.length : 0)),
            loading: false,
          });
          return { success: true };
        } catch (e) {
          if (get().__adminGuard !== guard) return { success: false, canceled: true };
          const msg = pickErr(e, 'Failed to load users.');
          set({ loading: false, adminUsersError: msg });
          return { success: false, error: msg };
        } finally {
          set((s) => (s.__adminGuard === guard ? { __adminGuard: null } : {}));
        }
      },

      // Single shared fetch for /dashboard-analysis with TTL cache.
      // All pages should call this instead of hitting the endpoint directly.
      fetchDashboardAnalysis: async ({ force = false } = {}) => {
        if (isDummyMode()) {
          const dummyDash = {
            dashboard: { total_calls: 248, success_rate: 87, avg_call_duration: 154, active_agents: 3, growth_vs_last_month: {} },
            your_ai_agents: { test_speed: DUMMY_ANALYTICS.agent_performance, view_all: 1, active: 1 },
            recent_activity: [],
          };
          set({ dashboardData: dummyDash, dashboardFetchedAt: Date.now() });
          return { success: true, data: dummyDash };
        }

        // Return cached data if still fresh
        const { dashboardFetchedAt, dashboardData, dashboardFetching } = get();
        if (!force && dashboardData && dashboardFetchedAt && (Date.now() - dashboardFetchedAt) < DASHBOARD_CACHE_TTL_MS) {
          return { success: true, data: dashboardData };
        }

        // Prevent duplicate in-flight requests
        if (dashboardFetching) {
          await new Promise((resolve) => {
            const unwatch = setInterval(() => {
              if (!get().dashboardFetching) { clearInterval(unwatch); resolve(); }
            }, 50);
          });
          return { success: true, data: get().dashboardData };
        }

        set({ dashboardFetching: true, dashboardLoading: true });
        try {
          const res = await api.get('/dashboard-analysis');
          const data = res?.data || {};
          set({ dashboardData: data, dashboardFetchedAt: Date.now(), dashboardFetching: false, dashboardLoading: false });
          return { success: true, data };
        } catch (err) {
          set({ dashboardFetching: false, dashboardLoading: false });
          return { success: false, error: err?.response?.data?.message || err.message };
        }
      },

      fetchAgents: async () => {
        if (isDummyMode()) {
          set({ agents: DUMMY_AGENTS, adminUserInfo: null, agentsLoading: false, agentsLoaded: true });
          return { success: true, data: DUMMY_AGENTS };
        }
        if (get().agentsScope.type !== 'self') return { success: false, ignored: true };
        return withScopeGuard(get, set, async () => {
          set({ agentsLoading: true, agentsLoaded: false });
          try {
            const res = await api.get('/analysis/training/agent-individual-analytics');
            const rows = res?.data?.individual_results || [];
            set({ agents: rows, adminUserInfo: null, agentsLoading: false, agentsLoaded: true });
            return { success: true, data: rows };
          } catch (err) {
            set({ agentsLoading: false, agentsLoaded: true });
            return { success: false, error: err?.response?.data?.message || err.message };
          }
        });
      },

      fetchAgentsForUser: async (userEmail) => {
        if (!userEmail) return { success: false, error: 'Missing email' };
        const scope = get().agentsScope;
        if (!(scope.type === 'user' && scope.email === userEmail)) return { success: false, ignored: true };
        return withScopeGuard(get, set, async () => {
          set({ agentsLoading: true, agentsLoaded: false });
          try {
            const res = await api.get('/auth/admin/user-agents', { params: { email: userEmail } });
            const data = res?.data || {};
            const rows = Array.isArray(data.agents) ? data.agents : [];
            set({
              agents: rows,
              adminUserInfo: {
                user_email: data.user_email,
                user_name: data.user_name,
                user_id: data.user_id,
                total_agents: data.total_agents ?? rows.length,
              },
              agentsLoading: false,
              agentsLoaded: true,
            });
            return { success: true, data: rows };
          } catch (err) {
            set({ agentsLoading: false, agentsLoaded: true });
            return { success: false, error: err?.response?.data?.message || err.message };
          }
        });
      },

      createAgent: async (payload = {}) => {
        if (isDummyMode()) {
          const newAgent = {
            ...DUMMY_AGENT,
            id: `dummy-agent-${Date.now()}`,
            agent_id: `dummy-agent-${Date.now()}`,
            agent_name: payload.name || payload.agent_name || 'New Demo Agent',
            name: payload.name || payload.agent_name || 'New Demo Agent',
          };
          set((s) => ({ agents: [...(s.agents || []), newAgent] }));
          return { success: true, data: newAgent };
        }
        const agent_name = payload.agent_name ?? payload.name ?? payload.agentName;
        const first_message = payload.first_message ?? payload.firstMessage;
        const user_scenario = payload.user_scenario;
        const email = payload.email;
        const file = payload.file ?? (Array.isArray(payload.knowledgeFiles) ? payload.knowledgeFiles[0] : payload.knowledgeFile);
        const voice_file = payload.voice_file ?? payload.voiceFile;

        const business_name = payload.business_name ?? payload.businessName ?? payload.companyName ?? '';
        const agent_type = payload.agent_type ?? payload.type ?? payload.agentType ?? '';
        const speaking_style = payload.speaking_style ?? payload.speakingStyle ?? '';
        const contact_phone_number = normalizePhone(
          payload.contact_phone_number ?? payload.phone ?? payload.contactPhoneNumber ?? ''
        );
        const cal_data = payload.calData;
        const cal_api_key = cal_data?.['Cal.com']?.['Cal.com API Key'];
        const cal_event_type_id = cal_data?.['Cal.com']?.['Cal.com Event Type ID'];

        if (!agent_name) return { success: false, error: 'agent_name is required' };
        if (!first_message) return { success: false, error: 'first_message is required' };
        if (!user_scenario) return { success: false, error: 'user_scenario is required' };
        if (!email) return { success: false, error: 'email is required' };

        try {
          const fd = new FormData();
          appendToFormData(fd, 'agent_name', agent_name);
          appendToFormData(fd, 'first_message', first_message);
          appendToFormData(fd, 'prompt', user_scenario);
          appendToFormData(fd, 'email', email);

          if (business_name) appendToFormData(fd, 'business_name', business_name);
          if (agent_type) appendToFormData(fd, 'agent_type', agent_type);
          if (speaking_style) appendToFormData(fd, 'speaking_style', speaking_style);
          if (contact_phone_number) appendToFormData(fd, 'contact_phone_number', contact_phone_number);

          if (file) appendToFormData(fd, 'file', file);
          if (voice_file) appendToFormData(fd, 'voice_file', voice_file);

          if (cal_api_key) appendToFormData(fd, 'cal_api_key', cal_api_key);
          if (cal_event_type_id) appendToFormData(fd, 'cal_event_type_id', cal_event_type_id);

          const res = await api.post('/agents/create-agent', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          // Cache agent_type locally since backend doesn't return it in the list endpoint
          if (agent_type && agent_name) {
            set((s) => ({
              agentTypeCache: { ...s.agentTypeCache, [agent_name]: agent_type },
            }));
          }

          try { await get().loadAgentsForCurrentScope(); } catch { }

          return { success: true, data: res?.data };
        } catch (e) {
          console.error('Agent creation error:', e);
          return { success: false, error: pickErr(e, 'Failed to create agent') };
        }
      },

      deleteAgent: async (agentId) => {
        if (!agentId) return { success: false, error: 'Missing agent id' };
        if (isDummyMode()) {
          set((s) => ({ agents: (s.agents || []).filter((a) => a.id !== agentId && a.agent_id !== agentId) }));
          return { success: true };
        }
        try {
          await api.delete(`/agents/delete-agent/${agentId}`);
          set((s) => ({
            agents: (s.agents || []).filter((a) => a.id !== agentId && a.agent_id !== agentId),
          }));
          return { success: true };
        } catch (err) {
          return { success: false, error: err?.response?.data?.message || err.message };
        }
      },

      pauseAgent: async (agentId) => {
        if (!agentId) return { success: false, error: 'Missing agent id' };
        if (isDummyMode()) {
          set((s) => ({ agents: (s.agents || []).map((a) => a.id === agentId || a.agent_id === agentId ? { ...a, is_active: false } : a) }));
          return { success: true };
        }
        try {
          const agentName = get()._agentNameMap().get(String(agentId));
          const routeParam = encodeURIComponent(agentName || agentId);
          await api.patch(`/agents/pause-twilio-number/${routeParam}`);
          set((s) => ({
            agents: (s.agents || []).map((a) =>
              a.id === agentId || a.agent_id === agentId ? { ...a, is_active: false } : a
            ),
          }));
          return { success: true };
        } catch (err) {
          return { success: false, error: err?.response?.data?.message || err.message };
        }
      },

      resumeAgent: async (agentId) => {
        if (!agentId) return { success: false, error: 'Missing agent id' };
        if (isDummyMode()) {
          set((s) => ({ agents: (s.agents || []).map((a) => a.id === agentId || a.agent_id === agentId ? { ...a, is_active: true } : a) }));
          return { success: true };
        }
        try {
          const agentName = get()._agentNameMap().get(String(agentId));
          const routeParam = encodeURIComponent(agentName || agentId);
          await api.patch(`/agents/resume-twilio-number/${routeParam}`);
          set((s) => ({
            agents: (s.agents || []).map((a) =>
              a.id === agentId || a.agent_id === agentId ? { ...a, is_active: true } : a
            ),
          }));
          return { success: true };
        } catch (err) {
          return { success: false, error: err?.response?.data?.message || err.message };
        }
      },

      updateAgentExact: async ({
        email,
        agent_name,
        first_message,
        prompt,
        file,
        voice_file,
        business_name,
        agent_type,
        speaking_style,
        contact_phone_number,
      } = {}) => {
        if (!email) return { success: false, error: 'email is required' };
        if (!agent_name) return { success: false, error: 'agent_name is required' };
        if (isDummyMode()) return { success: true, data: {} };
        try {
          const fd = new FormData();
          appendToFormData(fd, 'email', email);
          appendToFormData(fd, 'agent_name', agent_name);

          if (first_message) appendToFormData(fd, 'first_message', first_message);
          if (prompt) appendToFormData(fd, 'prompt', prompt);
          if (file) appendToFormData(fd, 'file', file);
          if (voice_file) appendToFormData(fd, 'voice_file', voice_file);
          if (business_name) appendToFormData(fd, 'business_name', business_name);
          if (agent_type) appendToFormData(fd, 'agent_type', agent_type);
          if (speaking_style) appendToFormData(fd, 'speaking_style', speaking_style);

          const phoneNorm = contact_phone_number ? normalizePhone(contact_phone_number) : '';
          if (phoneNorm) appendToFormData(fd, 'contact_phone_number', phoneNorm);

          const res = await api.put('/auth/agent/update-agent', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          try { await get().loadAgentsForCurrentScope(); } catch { }

          return { success: true, data: res?.data };
        } catch (e) {
          return { success: false, error: pickErr(e, 'Failed to update agent') };
        }
      },

      fetchAnalytics: async (params = {}) => {
        if (isDummyMode()) {
          set({ analytics: DUMMY_ANALYTICS, loading: false });
          return { success: true };
        }
        set({ loading: true });
        try {
          const res = await api.get('/dashboard-analysis');
          set({ analytics: res?.data || {}, loading: false });
          return { success: true };
        } catch (err) {
          set({ loading: false });
          return { success: false, error: err?.response?.data?.message || err.message };
        }
      },

      fetchAgentOverview: async () => {
        set({ loading: true });
        try {
          const res = await api.post('/analysis/analytics/agent-overview-analytics', {});
          set({ agentOverview: res?.data || null, loading: false });
          return { success: true };
        } catch (err) {
          set({ loading: false });
          return { success: false, error: err?.response?.data?.message || err.message };
        }
      },

      fetchAgentAnalytics: async (agentId) => {
        if (!agentId) return;
        set({ loading: true });
        try {
          const res = await api.post('/analysis/agent-analytics', { agent_id: agentId });
          const data = res?.data || {};
          set((state) => ({
            agentAnalytics: { ...state.agentAnalytics, [agentId]: data },
            loading: false,
          }));
          return { success: true };
        } catch (err) {
          set({ loading: false });
          return { success: false, error: err?.response?.data?.message || err.message };
        }
      },

      fetchSubscriptions: async () => {
        if (isDummyMode()) {
          set({ subscriptions: [DUMMY_SUBSCRIPTION], currentSubscription: DUMMY_SUBSCRIPTION, loading: false });
          return { success: true };
        }
        set({ loading: true });
        try {
          const current = await api.get('/payments/current-subscription');
          const subscription = current?.data?.subscription || null;
          set({
            subscriptions: subscription ? [subscription] : [],
            currentSubscription: subscription,
            loading: false,
          });
          return { success: true };
        } catch (err) {
          set({ loading: false });
          return { success: false, error: err?.response?.data?.message || err.message };
        }
      },

      callHistory: [],
      callHistoryLoading: false,

      _agentNameMap() {
        const arr = get().agents || [];
        const map = new Map();
        arr.forEach((a) => {
          const id = a?.agent_id ?? a?.id ?? a?.twilio_agent_id ?? a?.uuid;
          const name = a?.agent_name ?? a?.name ?? a?.agent ?? a?.title ?? '';
          if (id) map.set(String(id), String(name || '').trim());
        });
        return map;
      },

      fetchCallHistory: async (limit = 50) => {
        if (isDummyMode()) {
          const now = new Date();
          const dummyCalls = Array.from({ length: 8 }, (_, i) => ({
            id: `dummy-call-${i}`, call_id: `dummy-call-${i}`,
            agent_id: 'dummy-agent-1', agent_name: 'Demo Agent',
            type: i % 2 === 0 ? 'inbound' : 'outbound',
            duration_seconds: 90 + i * 20,
            phone_number: `+1555000${String(i).padStart(4, '0')}`,
            status: i === 3 ? 'failed' : 'completed',
            timestamp: new Date(now.getTime() - i * 3600000).toISOString(),
            summary: 'Demo call summary.',
          }));
          set({ callHistory: dummyCalls, callHistoryLoading: false });
          return { success: true };
        }
        try {
          set({ callHistoryLoading: true });

          let agentsArr = get().agents;
          if (!Array.isArray(agentsArr) || !agentsArr.length) {
            try {
              const result = await get().fetchDashboardAnalysis();
              const rawAgents = result?.data?.your_ai_agents?.test_speed || [];
              agentsArr = rawAgents.map((a) => ({
                agent_id: a.agent_id,
                agent_name: a.agent_name,
                is_active: Boolean(a.active),
              }));
              set({ agents: agentsArr, agentsLoaded: true });
            } catch {
              agentsArr = get().agents || [];
            }
          }
          const nameMap = get()._agentNameMap();

          const res = await api.get('/call-history', { params: { limit } });
          const payload = res?.data || {};
          const list = Array.isArray(payload) ? payload : (Array.isArray(payload.calls) ? payload.calls : []);

          const normalized = list.map((c, idx) => {
            const agent_id = c.agent_id ?? null;
            const call_date = c.call_date ?? c.created_at ?? c.start_time ?? c.timestamp ?? null;
            const d = typeof call_date === 'number'
              ? new Date(call_date * (call_date < 2000000000 ? 1000 : 1))
              : new Date(call_date);

            return {
              id: c.call_id ?? c.id ?? idx,
              call_id: c.call_id ?? c.id ?? idx,
              agent_id,
              agent_name: c.agent_name || (agent_id ? nameMap.get(String(agent_id)) : null) || '',
              type: c.call_type ?? c.type ?? c.direction ?? 'inbound',
              duration_seconds: Number(c.duration_seconds ?? c.duration ?? 0),
              duration_label: c.duration_label ?? null,
              phone_number: c.caller_number ?? c.phone_number ?? c.customer_phone ?? c.caller ?? '',
              status: c.summary ? 'completed' : (c.duration_seconds > 0 ? 'completed' : 'missed'),
              timestamp: Number.isNaN(d.getTime()) ? null : d.toISOString(),
              summary: c.summary ?? null,
            };
          });

          set({ callHistory: normalized });
          return { success: true };
        } catch (err) {
          set({ callHistory: [] });
          return {
            success: false,
            error: err?.response?.data?.message || err?.message || 'Failed to load call history',
          };
        } finally {
          set({ callHistoryLoading: false });
        }
      },

      setActiveCall: (call) => set({ activeCall: call }),

      getAgentById: (id) => {
        const { agents } = get();
        return agents?.find?.((a) => a?.agent_id === id || a?.id === id) || null;
      },

      getCallPatterns: () => (get().analytics?.call_patterns ?? []),

      getWeeklySeries: () => weeklySeriesFrom(get().analytics?.weekly_performance ?? []),

      getAgentTypesSeries: () => {
        const t = get().analytics?.agent_types ?? {};
        return Object.entries(t).map(([label, value]) => ({ label, value }));
      },

      fetchDashboardData: async () => {
        set({ dashboardLoading: true });
        try {
          await Promise.allSettled([
            get().fetchDashboardAnalysis(),
            get().fetchSubscriptions(),
          ]);
        } finally {
          set({ dashboardLoading: false });
        }
        return { success: true };
      },

      fetchNotifications: async () => {
        set((s) => ({ notifications: { ...s.notifications, loading: true, error: '' } }));
        try {
          const res = await api.get('/notifications?limit=50');
          const items = res?.data?.notifications || [];
          const unreadCount = res?.data?.unread_count ?? 0;
          set({ notifications: { items, unreadCount, loading: false, error: '' } });
        } catch (e) {
          set((s) => ({ notifications: { ...s.notifications, loading: false, error: pickErr(e, 'Failed to load notifications') } }));
        }
      },

      fetchUnreadCount: async () => {
        try {
          const res = await api.get('/notifications/unread-count');
          const unreadCount = res?.data?.unread_count ?? 0;
          set((s) => ({ notifications: { ...s.notifications, unreadCount } }));
        } catch { }
      },

      markNotificationRead: async (id) => {
        try {
          await api.patch(`/notifications/${id}/read`);
          set((s) => ({
            notifications: {
              ...s.notifications,
              items: s.notifications.items.map((n) => n.id === id ? { ...n, is_read: true } : n),
              unreadCount: Math.max(0, s.notifications.unreadCount - 1),
            },
          }));
        } catch { }
      },

      markAllNotificationsRead: async () => {
        try {
          await api.post('/notifications/read-all');
          set((s) => ({
            notifications: {
              ...s.notifications,
              items: s.notifications.items.map((n) => ({ ...n, is_read: true })),
              unreadCount: 0,
            },
          }));
        } catch { }
      },

      billing: {
        plans: [],
        plansLoading: false,
        plansError: '',

        currentSub: null,
        currentLoading: false,
        currentError: '',

        usage: null,
        usageLoading: false,
        usageError: '',

        compareData: null,
        compareLoading: false,
        compareError: '',

        payments: [],
        paymentsLoading: false,
        paymentsLoadingMore: false,
        paymentsError: '',
        totalPayments: 0,
        hasMorePayments: false,

        redirectingPlan: '',
      },

      billingBootstrap: async () => {
        if (isDummyMode()) {
          set((s) => ({
            billing: {
              ...s.billing,
              plans: DUMMY_PLANS, plansLoading: false, plansError: '',
              currentSub: DUMMY_SUBSCRIPTION, currentLoading: false, currentError: '',
              usage: { minutes_used: 124, minutes_limit: 500, period_end: '', current_plan: 'pro', current_period_end: '' }, usageLoading: false, usageError: '',
              compareData: null, compareLoading: false, compareError: '',
            },
            currentSubscription: DUMMY_SUBSCRIPTION,
          }));
          return { success: true };
        }
        set((s) => ({
          billing: {
            ...s.billing,
            plans: DUMMY_PLANS, plansLoading: false, plansError: '',
            currentLoading: true, currentError: '',
            usageLoading: true, usageError: '',
            compareData: null, compareLoading: false, compareError: '',
          },
        }));

        const cRes = await api.get('/payments/current-subscription').catch((e) => ({ __err: e }));
        const sub = cRes?.__err ? null : (cRes?.data?.subscription || null);

        const currentSub = sub ? {
          plan: sub.plan_name,
          plan_code: sub.plan_name,
          status: sub.status,
          cancel_at_period_end: false,
          stripe_subscription_id: sub.stripe_subscription_id,
          start_date: sub.start_date,
        } : null;

        const usage = sub ? {
          current_plan: sub.plan_name,
          minutes_limit: sub.included_minutes ?? 0,
          minutes_used: Math.max(0, (sub.included_minutes ?? 0) - (sub.remaining_minutes ?? 0)),
          minutes_remaining: sub.remaining_minutes ?? 0,
          overage_cost: null,
        } : null;

        set((s) => ({
          billing: {
            ...s.billing,
            currentSub,
            currentLoading: false,
            currentError: cRes?.__err ? pickErr(cRes.__err, 'Failed to load current subscription') : '',
            usage,
            usageLoading: false,
            usageError: '',
          },
          currentSubscription: currentSub,
        }));

        return { success: true };
      },

      billingRefreshCore: async () => {
        if (isDummyMode()) return { success: true };
        const cRes = await api.get('/payments/current-subscription').catch((e) => ({ __err: e }));
        const sub = cRes?.__err ? null : (cRes?.data?.subscription || null);

        const currentSub = sub ? {
          plan: sub.plan_name,
          plan_code: sub.plan_name,
          status: sub.status,
          cancel_at_period_end: false,
          stripe_subscription_id: sub.stripe_subscription_id,
          start_date: sub.start_date,
        } : (cRes?.__err ? get().billing.currentSub : null);

        const usage = sub ? {
          current_plan: sub.plan_name,
          minutes_limit: sub.included_minutes ?? 0,
          minutes_used: Math.max(0, (sub.included_minutes ?? 0) - (sub.remaining_minutes ?? 0)),
          minutes_remaining: sub.remaining_minutes ?? 0,
          overage_cost: null,
        } : (cRes?.__err ? get().billing.usage : null);

        set((s) => ({
          billing: {
            ...s.billing,
            currentSub,
            currentError: cRes?.__err ? pickErr(cRes.__err, 'Failed to load current subscription') : '',
            usage,
            usageError: '',
          },
          currentSubscription: currentSub,
        }));

        return { success: !cRes?.__err };
      },

      billingFetchPayments: async ({ startingAfter = '', limit = 50 } = {}, { append = false } = {}) => {
        if (isDummyMode()) {
          set((s) => ({ billing: { ...s.billing, payments: [], paymentsLoading: false, paymentsLoadingMore: false, totalPayments: 0, hasMorePayments: false } }));
          return { success: true };
        }
        // --- original below ---
        set((s) => ({
          billing: {
            ...s.billing,
            paymentsLoading: append ? s.billing.paymentsLoading : true,
            paymentsLoadingMore: append,
            paymentsError: append ? s.billing.paymentsError : '',
            ...(append ? {} : { payments: [] }),
          },
        }));

        const doFetch = async (lim) => {
          const params = new URLSearchParams();
          if (lim) params.set('limit', String(lim));
          return api.get(`/payments/subscription-history?${params.toString()}`);
        };

        try {
          let res;
          try {
            res = await doFetch(limit);
          } catch (e) {
            if (e?.response?.status === 500 && limit > 20) {
              res = await doFetch(20);
            } else {
              throw e;
            }
          }
          const d = res?.data || {};
          const rawList = Array.isArray(d.history) ? d.history : [];
          const list = rawList.map((h) => ({
            id: String(h.id ?? ''),
            invoice_id: h.stripe_checkout_session_id || '',
            created: h.created_at,
            description: h.description || `${h.plan_name || 'Plan'} subscription`,
            amount: typeof h.amount_cents === 'number' ? h.amount_cents / 100 : null,
            amount_formatted: typeof h.amount_cents === 'number'
              ? new Intl.NumberFormat('en-EU', { style: 'currency', currency: (h.currency || 'eur').toUpperCase(), maximumFractionDigits: 2 }).format(h.amount_cents / 100)
              : '—',
            currency: h.currency || 'eur',
            status: h.status || 'completed',
            payment_method: '',
            invoice_url: '',
            receipt_url: '',
          }));
          set((s) => ({
            billing: {
              ...s.billing,
              payments: append ? [...s.billing.payments, ...list] : list,
              paymentsLoading: false,
              paymentsLoadingMore: false,
              paymentsError: '',
              totalPayments: d.count || list.length,
              hasMorePayments: false,
            },
          }));
          return { success: true };
        } catch (e) {
          const msg = pickErr(e, 'Failed to load payment history');
          set((s) => ({
            billing: {
              ...s.billing,
              paymentsLoading: false,
              paymentsLoadingMore: false,
              paymentsError: msg,
              payments: append ? s.billing.payments : [],
            },
          }));
          return { success: false, error: msg };
        }
      },

      billingLoadMorePayments: async () => {
        const s = get().billing;
        if (!s.hasMorePayments || s.paymentsLoadingMore || !s.payments.length) return { success: false };
        const last = s.payments[s.payments.length - 1];
        const cursor = last?.cursor || last?.id || last?.invoice_id || '';
        if (!cursor) return { success: false };
        return get().billingFetchPayments({ limit: 50, startingAfter: cursor }, { append: true });
      },

      billingCreateCheckoutSession: async (plan) => {
        if (isDummyMode()) return { success: false, error: 'Checkout is not available in demo mode. Please sign up for a real account.' };
        const planName = typeof plan === 'string' ? plan : plan?.name;

        set((s) => ({ billing: { ...s.billing, redirectingPlan: planName } }));
        try {
          const body = { plan_name: planName };
          const res = await api.post('/payments/create-checkout-session', body);
          const url = res?.data?.checkout_url || res?.data?.url;
          if (!url) throw new Error('No checkout_url returned');
          set((s) => ({ billing: { ...s.billing, redirectingPlan: '' } }));
          return { success: true, url };
        } catch (e) {
          set((s) => ({ billing: { ...s.billing, redirectingPlan: '' } }));
          return { success: false, error: pickErr(e, 'Failed to start checkout') };
        }
      },

      billingConfirmSubscription: async (sessionId) => {
        if (!sessionId || isDummyMode()) return { success: false };
        try {
          await api.get(`/payments/confirm-subscription?session_id=${encodeURIComponent(sessionId)}`);
          return { success: true };
        } catch (e) {
          return { success: false, error: pickErr(e, 'Failed to confirm subscription') };
        }
      },

      billingDowngradeToFree: async () => {
        return { success: false, error: 'To cancel your subscription, please contact support at hello@valoria.ai' };
      },
    }),
    {
      name: 'app-storage',
      partialize: (state) => ({
        sidebarOpen: state.sidebarOpen,
        currentPage: state.currentPage,
        selectedAgent: state.selectedAgent,
        agentsScope: state.agentsScope,
        agentTypeCache: state.agentTypeCache,
      }),
    }
  )
);

export default useAppStore;
